import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { generateExcelFileName } from "../../../../utils/excelUtils";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = String(text).length * charWidth + padding;
  return Math.min(estimatedWidth, (8 * window.innerWidth) / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Human-readable labels for parameters
const parameterLabels = {
  plate1: "Plate 1",
  plate2: "Plate 2",
  plate3: "Plate 3",
  plate4: "Plate 4",
  plate5: "Plate 5",
  setTemperature: "Set Temperature (°C)",
  weldingTime: "Welding Time",
  remarks: "Remarks",
};

const StringerTable = ({ data = [], startDate, endDate, shifts = [] }) => {
  // Get all dates in the range
  const dates = useMemo(() => getDateRange(startDate, endDate), [startDate, endDate]);

  // Use provided shifts, fallback to unique shifts in data
  const effectiveShifts = useMemo(() => {
    if (shifts && shifts.length > 0) return shifts;
    const found = [...new Set(data.map((item) => item?.inspectionShift).filter(Boolean))].sort();
    return found.length > 0 ? found : ["1"];
  }, [shifts, data]);

  // Extract all unique stringers across the entire dataset
  const allUniqueStringers = useMemo(() => {
    const stringerSet = new Set();
    data.forEach((item) => {
      Object.values(item.form_data || {}).forEach((sData) => {
        if (sData?.stringer) stringerSet.add(sData.stringer);
      });
    });
    const arr = [...stringerSet].sort();
    return arr.length > 0 ? arr : ["Stringer 1"];
  }, [data]);

  // Column widths definition
  const columnWidths = useMemo(() => {
    return {
      section: 120,
      parameter: 180,
      value: 110,
    };
  }, []);

  const getLeftPosition = (index) => {
    if (index === 0) return 0;
    return columnWidths.section;
  };

  // Static parameter rows
  const tableRows = useMemo(() => {
    const parameters = [
      "setTemperature",
      "weldingTime",
      "plate1",
      "plate2",
      "plate3",
      "plate4",
      "plate5",
      "remarks",
    ];
    const rows = [];
    ["sectionA", "sectionB"].forEach((sectionKey) => {
      const sectionLabel = sectionKey === "sectionA" ? "Section A" : "Section B";
      parameters.forEach((param) => {
        rows.push({
          section: sectionLabel,
          parameter: parameterLabels[param] || param,
          paramKey: param,
          sectionKey,
        });
      });
    });
    return rows;
  }, []);

  // Map data to quick lookup: [sectionKey_stringer_date_shift_paramKey]
  const tableData = useMemo(() => {
    const dataMap = {};
    data.forEach((item) => {
      const date = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      if (!date || !shift) return;

      const dateShiftKey = `${date}_${shift}`;
      Object.values(item.form_data || {}).forEach((stringerData) => {
        if (!stringerData?.stringer) return;
        const stringer = stringerData.stringer;

        ["sectionA", "sectionB"].forEach((sectionKey) => {
          const sectionData = stringerData[sectionKey] || {};
          const keyPrefix = `${sectionKey}_${stringer}_${dateShiftKey}`;

          ["weldingTime", "remarks", "setTemperature"].forEach((param) => {
            if (sectionData[param] !== undefined) {
              dataMap[`${keyPrefix}_${param}`] = sectionData[param];
            }
          });

          Object.entries(sectionData.heatingPlate || {}).forEach(([plate, value]) => {
            dataMap[`${keyPrefix}_${plate}`] = value;
          });
        });
      });
    });
    return dataMap;
  }, [data]);

  // Dynamic header tree: guarantees every date and shift gets uniform columns
  const headerColumns = useMemo(() => {
    const cols = [];
    dates.forEach((date) => {
      effectiveShifts.forEach((shift) => {
        allUniqueStringers.forEach((stringer) => {
          cols.push({
            date,
            shift,
            stringer,
            key: `${date}_${shift}_${stringer}`,
          });
        });
      });
    });
    return cols;
  }, [dates, effectiveShifts, allUniqueStringers]);

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const merges = [];

    const row1 = ["Section", "Parameter"];
    const row2 = ["", ""];
    const row3 = ["", ""];

    merges.push({ s: { r: 0, c: 0 }, e: { r: 2, c: 0 } });
    merges.push({ s: { r: 0, c: 1 }, e: { r: 2, c: 1 } });

    let colIdx = 2;
    const stringersCount = allUniqueStringers.length;
    const shiftColSpan = stringersCount;
    const dateColSpan = effectiveShifts.length * stringersCount;

    dates.forEach((date) => {
      row1.push(date);
      for (let i = 1; i < dateColSpan; i++) row1.push("");
      merges.push({ s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + dateColSpan - 1 } });

      effectiveShifts.forEach((shift) => {
        row2.push(`Shift ${shift}`);
        for (let i = 1; i < shiftColSpan; i++) row2.push("");
        merges.push({ s: { r: 1, c: colIdx }, e: { r: 1, c: colIdx + shiftColSpan - 1 } });

        allUniqueStringers.forEach((stringer) => {
          row3.push(stringer);
          colIdx += 1;
        });
      });
    });

    const dataRows = tableRows.map((row) => {
      const r = [row.section, row.parameter];
      headerColumns.forEach((col) => {
        const lookupKey = `${row.sectionKey}_${col.stringer}_${col.date}_${col.shift}_${row.paramKey}`;
        r.push(tableData[lookupKey] ?? "N/A");
      });
      return r;
    });

    const sheetData = [row1, row2, row3, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    if (merges.length > 0) ws["!merges"] = merges;

    ws["!cols"] = [
      { wch: 16 },
      { wch: 25 },
      ...Array(headerColumns.length).fill({ wch: 14 }),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Stringer Parameters");

    const fileName = generateExcelFileName("Stringer_Parameters", startDate, endDate);
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  const stringersPerShift = allUniqueStringers.length;
  const colsPerDate = effectiveShifts.length * stringersPerShift;

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Stringer Inspection Data
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0 || dates.length === 0}
      >
        Download as Excel
      </Button>

      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            {/* Row 1: Date */}
            <TableRow>
              <StickyTableCell
                rowSpan={3}
                columnWidth={columnWidths.section}
                left={getLeftPosition(0)}
                isLastSticky={false}
              >
                Section
              </StickyTableCell>
              <StickyTableCell
                rowSpan={3}
                columnWidth={columnWidths.parameter}
                left={getLeftPosition(1)}
                isLastSticky={true}
              >
                Parameter
              </StickyTableCell>
              {dates.map((date) => (
                <CustomTableCell
                  key={date}
                  colSpan={colsPerDate}
                  columnWidth={columnWidths.value * colsPerDate}
                >
                  {date}
                </CustomTableCell>
              ))}
            </TableRow>

            {/* Row 2: Shift */}
            <TableRow>
              {dates.flatMap((date) =>
                effectiveShifts.map((shift) => (
                  <CustomTableCell
                    key={`${date}_${shift}`}
                    colSpan={stringersPerShift}
                    columnWidth={columnWidths.value * stringersPerShift}
                  >
                    Shift {shift}
                  </CustomTableCell>
                ))
              )}
            </TableRow>

            {/* Row 3: Stringer */}
            <TableRow>
              {dates.flatMap((date) =>
                effectiveShifts.flatMap((shift) =>
                  allUniqueStringers.map((stringer) => (
                    <CustomTableCell
                      key={`${date}_${shift}_${stringer}`}
                      columnWidth={columnWidths.value}
                    >
                      {stringer}
                    </CustomTableCell>
                  ))
                )
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {dates.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={2}
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.section + columnWidths.parameter}
                  left={0}
                  isLastSticky={true}
                >
                  Please select a valid date range.
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={`${row.section}_${row.paramKey}_${index}`} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.section}
                    left={getLeftPosition(0)}
                    isLastSticky={false}
                  >
                    {row.section}
                  </StickyBodyTableCell>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.parameter}
                    left={getLeftPosition(1)}
                    isLastSticky={true}
                  >
                    {row.parameter}
                  </StickyBodyTableCell>
                  {headerColumns.map((col) => {
                    const lookupKey = `${row.sectionKey}_${col.stringer}_${col.date}_${col.shift}_${row.paramKey}`;
                    const val = tableData[lookupKey];
                    return (
                      <BodyTableCell key={`${col.key}_${row.sectionKey}_${row.paramKey}`} columnWidth={columnWidths.value}>
                        {val !== undefined && val !== "" ? String(val) : "N/A"}
                      </BodyTableCell>
                    );
                  })}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default StringerTable;